// ─── SourcePhoto.jsx ─────────────────────────────────────────────────────────
// Everything for the "See in photo" pop-up, shared by Materials,
// Fittings & Furnishings and Sample Delivery:
//
//   SourcePhotoLink  — the small "[photo] See in photo" link on a card
//   SourcePhotoPeek  — opens the Moodboard Lightbox in focused mode, on top
//                      of the current page (nothing navigates away)
//   useSourcePeek    — the page-side state: which item is open, which card
//                      glows after closing, and the samples notice
//   useGlow          — makes a card glow once and scroll into view
//   RemovalNotice    — "X was removed from your shortlist and your samples
//                      request." bar, shown until dismissed
//
// It lives in its own file (not ShortlistShared3.jsx) because it uses the
// Lightbox, and the Lightbox itself uses ShortlistShared3.jsx.

import { useState, useEffect, useRef, useCallback } from 'react';
import { keyframes } from '@mui/system';
import Box        from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon  from '@mui/icons-material/Close';

import Lightbox from './Lightbox5';
import { GREEN, CHARCOAL, BORDER, MUTED, bodyFont } from '../../../theme/tokens2';

// ─── SourcePhotoLink ─────────────────────────────────────────────────────────
// Shows only when the item came from a Moodboard photo that still exists.
// The customer's own uploads, and items whose photo was deleted, show nothing.
//   showRoom=false → "See in photo" (Materials: the room tab already says which room)
//   showRoom=true  → "See in Kitchen photo" (pages not split by room)
export function SourcePhotoLink({ item, scenesData, showRoom = false, onOpen }) {
  if (!item.source_scene_id || !scenesData?.length) return null;
  const scene = scenesData.find((s) => s.id === item.source_scene_id);
  if (!scene) return null;

  const text = showRoom ? `See in ${scene.cat_room} photo` : 'See in photo';

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(item, scene); }}
      aria-label={`See ${item.namelabel || 'this item'} in its ${scene.cat_room} moodboard photo`}
      sx={{
        display: 'flex', alignItems: 'center', gap: '7px', mt: '9px',
        p: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        color: GREEN, fontSize: '10px', fontWeight: 600, ...bodyFont,
        borderRadius: '2px',
        '&:hover .spl-text': { textDecoration: 'underline' },
        '&:hover .spl-thumb': { boxShadow: `0 0 0 1.5px ${GREEN}` },
        '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
      }}
    >
      <Box
        component="img"
        className="spl-thumb"
        src={scene.imagesrc}
        alt=""
        loading="lazy"
        sx={{
          width: 24, height: 24, objectFit: 'cover', borderRadius: '2px',
          border: `0.5px solid ${BORDER}`, flexShrink: 0,
          transition: 'box-shadow 0.15s ease',
        }}
      />
      <span className="spl-text">{text}</span>
    </Box>
  );
}

// ─── useGlow ─────────────────────────────────────────────────────────────────
// After the pop-up closes, the card the customer came from glows green once
// (about 2.4 seconds) and scrolls into view if needed, so their eye lands
// back in the right place.
//   const [cardRef, glowSx] = useGlow(isGlowing);
//   <Box ref={cardRef} sx={{ ...otherStyles, ...glowSx }}>
const glow = keyframes`
  0%   { box-shadow: 0 0 0 3px rgba(33,131,51,1); }
  70%  { box-shadow: 0 0 0 3px rgba(33,131,51,0.6); }
  100% { box-shadow: 0 0 0 3px rgba(33,131,51,0); }
`;

export function useGlow(isGlowing) {
  const ref = useRef(null);
  useEffect(() => {
    if (isGlowing && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isGlowing]);
  return [ref, isGlowing ? { animation: `${glow} 2.4s ease-out 1` } : {}];
}

// ─── useSourcePeek ───────────────────────────────────────────────────────────
// onChanged: called after the pop-up closes if anything was saved or removed,
// so the page can reload its list.
export function useSourcePeek(onChanged) {
  const [peek,       setPeek]       = useState(null); // { item, scene, rankLabel }
  const [glowItemId, setGlowItemId] = useState(null);
  const [notice,     setNotice]     = useState(null); // [{ id, name }] or null
  const removedRef = useRef([]);
  const timer      = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const openPeek = useCallback((item, scene, rankLabel) => {
    setPeek({ item, scene, rankLabel });
  }, []);

  // The Lightbox calls this first (only if something changed) ...
  const handleClosedWithChanges = useCallback((changes) => {
    removedRef.current = changes.removed || [];
    onChanged?.();
    if (changes.removedFromSamples?.length) {
      setNotice((prev) => {
        const merged = [...(prev || [])];
        changes.removedFromSamples.forEach((r) => {
          if (!merged.some((m) => m.id === r.id)) merged.push(r);
        });
        return merged;
      });
    }
  }, [onChanged]);

  // ... then this. A removed item has no card left to glow.
  const closePeek = useCallback(() => {
    const id = peek?.item?.id;
    const wasRemoved = removedRef.current.includes(id);
    removedRef.current = [];
    setPeek(null);
    if (id != null && !wasRemoved) {
      clearTimeout(timer.current);
      setGlowItemId(id);
      timer.current = setTimeout(() => setGlowItemId(null), 2400);
    }
  }, [peek]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return { peek, openPeek, closePeek, handleClosedWithChanges, glowItemId, notice, dismissNotice };
}

// ─── SourcePhotoPeek ─────────────────────────────────────────────────────────
//   shortlistName — 'Materials' or 'Furniture' (used in the panel's wording)
//   returnLabel   — the page's name, for the "← Back to …" button
export function SourcePhotoPeek({ peek, shortlistName, returnLabel, onClose, onClosedWithChanges }) {
  if (!peek) return null;
  return (
    <Lightbox
      photos={[peek.scene]}
      initialPhotoIndex={0}
      selectedCategory={peek.scene.cat_room}
      focus={{
        regionId: peek.item.region_id ?? null,
        itemId: peek.item.id,
        rankLabel: peek.rankLabel,
        shortlistName,
      }}
      returnLabel={returnLabel}
      onClosedWithChanges={onClosedWithChanges}
      onClose={onClose}
    />
  );
}

// ─── RemovalNotice ───────────────────────────────────────────────────────────
// Shown at the top of the list when items that were in the samples request
// were removed in the pop-up. Stays until the customer dismisses it.
export function RemovalNotice({ items, onDismiss, sx = {} }) {
  if (!items?.length) return null;
  return (
    <Box
      role="status"
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        px: '12px', py: '9px',
        background: 'rgba(33,131,51,0.04)',
        borderLeft: '2px solid rgba(33,131,51,0.35)',
        ...sx,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {items.map((r) => (
          <Typography key={r.id} sx={{ fontSize: '11px', color: MUTED, lineHeight: 1.55, ...bodyFont }}>
            <Box component="span" sx={{ fontWeight: 600, color: CHARCOAL }}>{r.name}</Box>
            {' '}was removed from your shortlist and your samples request.
          </Typography>
        ))}
      </Box>
      <Box
        component="button"
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss this message"
        sx={{
          background: 'none', border: 'none', cursor: 'pointer', p: 0,
          width: 24, height: 24, flexShrink: 0, color: MUTED,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover': { color: CHARCOAL },
          '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '1px' },
        }}
      >
        <CloseIcon sx={{ fontSize: '0.875rem' }} />
      </Box>
    </Box>
  );
}
